import { useState } from 'react';

type Status = { state: 'idle' } | { state: 'submitting' } | { state: 'ok' } | { state: 'error'; message: string };
type Values = { firstName: string; lastName: string; mobile: string; instagram: string };

const EMPTY: Values = { firstName: '', lastName: '', mobile: '', instagram: '' };

type Props = {
  /** Called when the form successfully submits, so the parent can navigate
   *  to the standalone /thank-you page. */
  onSubmitted: () => void;
};

export function Guestlist({ onSubmitted }: Props) {
  const [status, setStatus] = useState<Status>({ state: 'idle' });
  const [values, setValues] = useState<Values>(EMPTY);

  // The submit button stays disabled until all required fields contain
  // non-whitespace input. Mobile additionally must contain at least 7 digits
  // (CH numbers are 9 digits without country code; we tolerate spaces/hyphens).
  const mobileDigits = values.mobile.replace(/\D/g, '');
  const isValid =
    values.firstName.trim().length > 0 &&
    values.lastName.trim().length > 0 &&
    mobileDigits.length >= 7;

  function update<K extends keyof Values>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setValues((v) => ({ ...v, [key]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!isValid) return;
    setStatus({ state: 'submitting' });
    try {
      const res = await fetch('/api/guestlist', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setStatus({ state: 'ok' });
      setValues(EMPTY);
      onSubmitted();
    } catch (err) {
      setStatus({ state: 'error', message: err instanceof Error ? err.message : 'Something went wrong.' });
    }
  }

  const submitting = status.state === 'submitting';

  return (
    <section className="panel panel--parchment" id="reserve">
      <div className="parchment-frame">
        <div className="invitation">
          <h2 className="invitation__title">Request your Invitation</h2>
          <p className="invitation__lede">
            Please provide your details below. As we carefully select our crowd to preserve the
            atmosphere, we will notify you via SMS if your access is approved.
          </p>

          <form className="invite-form" onSubmit={handleSubmit}>
            <div className="invite-form__row">
              <label className="invite-field">
                <span className="invite-field__label">
                  First Name<span className="invite-field__required" aria-hidden="true">*</span>
                </span>
                <input
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={values.firstName}
                  onChange={update('firstName')}
                />
              </label>
              <label className="invite-field">
                <span className="invite-field__label">
                  Last Name<span className="invite-field__required" aria-hidden="true">*</span>
                </span>
                <input
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={values.lastName}
                  onChange={update('lastName')}
                />
              </label>
            </div>

            <div className="invite-form__row">
              <label className="invite-field invite-field--prefixed">
                <span className="invite-field__label">
                  Mobile<span className="invite-field__required" aria-hidden="true">*</span>
                </span>
                <span className="invite-field__row">
                  <span className="invite-field__prefix">+41</span>
                  <input
                    name="mobile"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    required
                    minLength={7}
                    className="invite-field__input invite-field__input--prefixed"
                    value={values.mobile}
                    onChange={update('mobile')}
                  />
                </span>
              </label>
              <label className="invite-field invite-field--prefixed">
                <span className="invite-field__label">
                  Instagram <span className="invite-field__optional">(optional)</span>
                </span>
                <span className="invite-field__row">
                  <span className="invite-field__prefix">@</span>
                  <input
                    name="instagram"
                    type="text"
                    autoCapitalize="off"
                    autoCorrect="off"
                    className="invite-field__input invite-field__input--prefixed"
                    value={values.instagram}
                    onChange={update('instagram')}
                  />
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="cta-link"
              disabled={submitting || !isValid}
              aria-disabled={submitting || !isValid}
              title={isValid ? undefined : 'Compila First Name, Last Name e Mobile'}
            >
              {submitting ? 'submitting…' : 'submit request'}
            </button>

            {status.state === 'error' && (
              <p className="invitation__confirmation invitation__confirmation--error">
                Could not submit: {status.message}
              </p>
            )}
          </form>

          <p className="invitation__sign">Hasta luego</p>
        </div>
      </div>
    </section>
  );
}
